<template>
  <div class="variable-picker-trigger">
    <!-- Trigger button -->
    <div
      data-test-id="variables-dropdown"
      class="variable-dropdown vp-trigger"
      role="button"
      tabindex="0"
      @click="openModal"
      @keydown.enter="openModal"
      @keydown.space.prevent="openModal"
    >
      <span class="vp-trigger-label">{{ placeholder }}</span>
      <i
        v-if="placeholder.length > 2"
        class="pi pi-chevron-down vp-trigger-chevron"
      />
    </div>

    <!-- Variable picker modal -->
    <Dialog
      v-model:visible="isModalVisible"
      :modal="true"
      :draggable="false"
      :closable="true"
      :style="{ width: '520px', maxWidth: '90vw' }"
      class="variable-picker-dialog"
      @show="onModalShow"
    >
      <template #header>
        <div class="vp-header">
          <i class="fa-light fa-code" />
          <span>{{ t('templateBuilder.variablePicker.title') }}</span>
        </div>
      </template>

      <!-- Format panel (step 2) : replaces the list while a variable is being configured -->
      <VariableFormatPanel
        v-if="formatVariable"
        :variable="formatVariable"
        @back="formatVariable = null"
        @insert="onFormatInsert"
      />

      <!-- Search bar -->
      <div
        v-show="!formatVariable"
        class="vp-search"
      >
        <span class="p-input-icon-left w-full">
          <i class="fa-light fa-search" />
          <InputText
            ref="searchInputRef"
            v-model="searchQuery"
            :placeholder="t('templateBuilder.variablePicker.searchPlaceholder')"
            class="w-full"
          />
        </span>
      </div>

      <!-- Scrollable body -->
      <div
        v-show="!formatVariable"
        class="vp-body"
      >
        <!-- Normal mode: accordions -->
        <template v-if="!searchQuery.trim()">
          <div
            v-for="group in groupedVariables"
            :key="group.key"
            class="vp-accordion"
          >
            <div
              class="vp-accordion-header"
              @click="toggleGroup(group.key)"
            >
              <i
                class="fa-solid fa-chevron-right vp-chevron"
                :class="{ open: openGroups.has(group.key) }"
              />
              <div
                class="vp-group-icon"
                :style="{ background: group.bg, color: group.color }"
              >
                <i :class="group.icon" />
              </div>
              <span class="vp-group-label">{{ group.label }}</span>
              <span class="vp-count">{{ group.items.length }}</span>
            </div>
            <div
              v-if="openGroups.has(group.key)"
              class="vp-accordion-content"
            >
              <div
                v-for="item in group.items"
                :key="item.value"
                class="vp-item"
                @click="selectVariable(item.value)"
              >
                <div class="vp-item-left">
                  <span class="vp-item-label">{{ item.displayLabel || t(item.label) }}</span>
                  <span class="vp-item-value">{{ item.value }}</span>
                </div>
                <span
                  v-if="typeBadgeFor(item)"
                  class="vp-badge"
                  :class="item.preformatted ? 'vp-badge-preformatted' : 'vp-badge-type'"
                >
                  {{ typeBadgeFor(item) }}
                </span>
                <span
                  v-if="item.isCustom && item.isFromCollection"
                  class="vp-badge vp-badge-iteration"
                >
                  ITERATION
                </span>
                <span
                  v-else-if="item.isMetadata"
                  class="vp-badge vp-badge-metadata"
                >
                  METADATA
                </span>
                <span
                  v-else-if="item.isCustom"
                  class="vp-badge vp-badge-custom"
                >
                  CUSTOM
                </span>
                <div
                  v-if="formatPanelAvailable"
                  role="button"
                  tabindex="0"
                  class="vp-format-btn"
                  @click.stop="openFormatPanel(item)"
                  @keydown.enter.stop.prevent="openFormatPanel(item)"
                >
                  <span class="vp-format-btn-icon">ƒ</span>
                  {{ t('templateBuilder.variableFormat.formatButton') }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Search mode: same accordion look, all groups open -->
        <template v-else-if="filteredGroups.length > 0">
          <div
            v-for="group in filteredGroups"
            :key="group.key"
            class="vp-accordion"
          >
            <div class="vp-accordion-header vp-accordion-header--static">
              <div
                class="vp-group-icon"
                :style="{ background: group.bg, color: group.color }"
              >
                <i :class="group.icon" />
              </div>
              <span class="vp-group-label">{{ group.label }}</span>
              <span class="vp-count">{{ group.items.length }}</span>
            </div>
            <div class="vp-accordion-content">
              <div
                v-for="item in group.items"
                :key="item.value"
                class="vp-item"
                @click="selectVariable(item.value)"
              >
                <div class="vp-item-left">
                  <span class="vp-item-label">{{ item.displayLabel || t(item.label) }}</span>
                  <span class="vp-item-value">{{ item.value }}</span>
                </div>
                <span
                  v-if="typeBadgeFor(item)"
                  class="vp-badge"
                  :class="item.preformatted ? 'vp-badge-preformatted' : 'vp-badge-type'"
                >
                  {{ typeBadgeFor(item) }}
                </span>
                <span
                  v-if="item.isCustom && item.isFromCollection"
                  class="vp-badge vp-badge-iteration"
                >
                  ITERATION
                </span>
                <span
                  v-else-if="item.isMetadata"
                  class="vp-badge vp-badge-metadata"
                >
                  METADATA
                </span>
                <span
                  v-else-if="item.isCustom"
                  class="vp-badge vp-badge-custom"
                >
                  CUSTOM
                </span>
                <div
                  v-if="formatPanelAvailable"
                  role="button"
                  tabindex="0"
                  class="vp-format-btn"
                  @click.stop="openFormatPanel(item)"
                  @keydown.enter.stop.prevent="openFormatPanel(item)"
                >
                  <span class="vp-format-btn-icon">ƒ</span>
                  {{ t('templateBuilder.variableFormat.formatButton') }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-else
          class="vp-empty"
        >
          <i class="fa-light fa-search" />
          <span>{{ t('templateBuilder.variablePicker.noResults') }}</span>
        </div>
      </div>

      <template #footer>
        <div
          v-if="!formatVariable"
          class="vp-footer"
        >
          <Button
            :label="t('templateBuilder.variablePicker.cancel')"
            text
            class="p-button-secondary"
            @click="isModalVisible = false"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  computed,
  ref,
  nextTick,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { DropdownOption } from '@/types';
import { groupVariables, EnrichedVariable } from '@/components/template-builder/utils/variables-list';
import VariableFormatPanel from '@/components/fields/partials/VariableFormatPanel.vue';
import { isFormatPanelAvailable } from '@/components/fields/partials/variable-format';

export default defineComponent({
  name: 'VariablesButton',

  components: {
    Dialog,
    InputText,
    Button,
    VariableFormatPanel,
  },

  props: {
    variablesList: {
      type: Array as PropType<DropdownOption[]>,
      required: true,
    },

    placeholder: {
      type: String,
      required: false,
      default: 'Variables',
    },
  },

  emits: {
    'on-choose-variable': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const isModalVisible = ref(false);
    const searchQuery = ref('');
    const openGroups = ref(new Set<string>());
    const searchInputRef = ref();
    const formatVariable = ref<EnrichedVariable | null>(null);

    const variables = computed<EnrichedVariable[]>(() => props.variablesList.map((variable: any) => ({
      value: variable.value,
      label: variable.label,
      displayLabel: (variable.isCustom || variable.isMetadata) ? variable.label : t(variable.label),
      isCustom: variable.isCustom || false,
      isFromCollection: variable.isFromCollection || false,
      isMetadata: variable.isMetadata || false,
      type: variable.type,
      preformatted: variable.preformatted,
    })));

    const groupedVariables = computed(() => groupVariables(variables.value, t));

    const filteredGroups = computed(() => {
      const query = searchQuery.value.toLowerCase().trim();
      if (!query) return groupedVariables.value;
      return groupedVariables.value
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => (item.displayLabel || t(item.label)).toLowerCase().includes(query)
              || item.value.toLowerCase().includes(query),
          ),
        }))
        .filter((group) => group.items.length > 0);
    });

    const openModal = () => {
      isModalVisible.value = true;
    };

    const onModalShow = () => {
      searchQuery.value = '';
      formatVariable.value = null;
      // Open first group by default
      openGroups.value = new Set(
        groupedVariables.value.length > 0 ? [groupedVariables.value[0].key] : [],
      );
      nextTick(() => {
        searchInputRef.value?.$el?.focus();
      });
    };

    const toggleGroup = (key: string) => {
      const next = new Set(openGroups.value);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      openGroups.value = next;
    };

    const selectVariable = (value: string | number) => {
      context.emit('on-choose-variable', value);
      isModalVisible.value = false;
    };

    // Format panel (step 2) — only on channels rendered by the v5 engine
    const formatPanelAvailable = computed(() => isFormatPanelAvailable());

    const openFormatPanel = (item: EnrichedVariable) => {
      formatVariable.value = item;
    };

    const onFormatInsert = (value: string) => {
      formatVariable.value = null;
      selectVariable(value);
    };

    const typeBadgeFor = (item: EnrichedVariable): string | null => {
      if (item.preformatted) {
        return t('templateBuilder.variableFormat.preformattedBadge');
      }
      if (item.type && ['number', 'decimal'].includes(item.type)) {
        return t('templateBuilder.variableFormat.typeNumber');
      }
      if (item.type && ['date', 'datetime'].includes(item.type)) {
        return t('templateBuilder.variableFormat.typeDate');
      }
      return null;
    };

    return {
      t,
      isModalVisible,
      searchQuery,
      openGroups,
      searchInputRef,
      formatVariable,
      formatPanelAvailable,
      groupedVariables,
      filteredGroups,
      openModal,
      onModalShow,
      toggleGroup,
      selectVariable,
      openFormatPanel,
      onFormatInsert,
      typeBadgeFor,
    };
  },
});
</script>

<style>
/* Trigger button — adapts to both toolbar (compact "T") and standalone (full dropdown look) */
.vp-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  background: #fff;
  font-size: 0.85rem;
  color: #6b7280;
  transition: border-color 0.15s;
  user-select: none;
}
.vp-trigger:hover {
  border-color: #9ca3af;
}
.vp-trigger-chevron {
  font-size: 0.6rem;
  color: #9ca3af;
}
/* Inside Quill toolbar: inherit the compact ql-custom-button sizing */
.ql-custom-button .vp-trigger {
  border: none;
  padding: 0;
  background: none;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Dialog content — NOT scoped because PrimeVue Dialog teleports to <body> */
.variable-picker-dialog .vp-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}
.variable-picker-dialog .vp-header i {
  color: #6366f1;
  font-size: 1.1rem;
}

.variable-picker-dialog .vp-search {
  padding: 0 0 12px;
}
.variable-picker-dialog .vp-search .p-input-icon-left i {
  top: 50% !important;
  transform: translateY(-50%);
  margin-top: 0 !important;
  color: #9ca3af;
  font-size: 0.85rem;
}

.variable-picker-dialog .vp-body {
  max-height: 380px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-align: left;
}

.variable-picker-dialog .vp-accordion {
  border-bottom: 1px solid #f3f4f6;
}
.variable-picker-dialog .vp-accordion:last-child {
  border-bottom: none;
}
.variable-picker-dialog .vp-accordion-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  transition: background 0.1s;
}
.variable-picker-dialog .vp-accordion-header:hover {
  background: #f9fafb;
}
.variable-picker-dialog .vp-chevron {
  font-size: 0.65rem;
  color: #9ca3af;
  transition: transform 0.2s;
  width: 12px;
  flex-shrink: 0;
}
.variable-picker-dialog .vp-chevron.open {
  transform: rotate(90deg);
}
.variable-picker-dialog .vp-group-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}
.variable-picker-dialog .vp-group-label {
  font-size: 0.83rem;
  font-weight: 600;
  color: #374151;
  flex: 1;
}
.variable-picker-dialog .vp-count {
  font-size: 0.7rem;
  background: #e5e7eb;
  color: #6b7280;
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: 500;
}

.variable-picker-dialog .vp-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px 8px 54px;
  cursor: pointer;
  transition: background 0.1s;
  text-align: left;
}
.variable-picker-dialog .vp-item:hover {
  background: #eef2ff;
}
.variable-picker-dialog .vp-accordion-header--static {
  cursor: default;
}
.variable-picker-dialog .vp-accordion-header--static:hover {
  background: transparent;
}
.variable-picker-dialog .vp-item-left {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  text-align: left;
}
.variable-picker-dialog .vp-item-label {
  font-size: 0.82rem;
  color: #1f2937;
  font-weight: 500;
}
.variable-picker-dialog .vp-item-value {
  font-size: 0.7rem;
  color: #9ca3af;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.variable-picker-dialog .vp-badge {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  flex-shrink: 0;
}
.variable-picker-dialog .vp-badge-custom {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
}
.variable-picker-dialog .vp-badge-iteration {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
}
.variable-picker-dialog .vp-badge-metadata {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}
.variable-picker-dialog .vp-badge-type {
  background: #e0f2fe;
  color: #0369a1;
}
.variable-picker-dialog .vp-badge-preformatted {
  background: #f3f4f6;
  color: #9ca3af;
}

/* "ƒ Format" button — revealed on row hover, opens the format panel */
.variable-picker-dialog .vp-format-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #ddd3fb;
  border-radius: 6px;
  background: #fff;
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 9px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
  visibility: hidden;
  transition: background 0.1s;
}
.variable-picker-dialog .vp-item:hover .vp-format-btn {
  visibility: visible;
}
.variable-picker-dialog .vp-format-btn:hover {
  background: #ede9fe;
}
.variable-picker-dialog .vp-format-btn:focus-visible {
  visibility: visible;
  outline: 2px solid #7c3aed;
  outline-offset: 1px;
}
.variable-picker-dialog .vp-format-btn-icon {
  font-style: italic;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 0.8rem;
  line-height: 1;
}

.variable-picker-dialog .vp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 40px;
  color: #9ca3af;
  gap: 8px;
}
.variable-picker-dialog .vp-empty i {
  font-size: 2rem;
}
.variable-picker-dialog .vp-empty span {
  font-size: 0.85rem;
}

.variable-picker-dialog .vp-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
